<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import Drawer from './Drawer/index.vue'
import DrawerLink from './DrawerLink/index.vue'
import DrawerItemAccordion from './DrawerItemAccordion/index.vue'
import CreateElementalMenu from './CreateElementalsMenu/index.vue'
import { useLeftSidebarStore } from '~~/src/stores/leftSidebar'
import TheDailyBite from '~/components/App/NewHeader/TheDailyBite/index.vue'
import PromptLibraryMenu from '~/components/App/NewHeader/PromptLibraryMenu/index.vue'
defineOptions({
  name: 'DrawerSidebarLeft',
})

const state = useLeftSidebarStore()
const { isSmaller } = useBreakpoints(breakpointsTailwind)
const isMobile = ref(isSmaller('md'))

if (process.client) {
  useResizeObserver(window.document.body, () => {
    isMobile.value = isSmaller('md')
  })
}

const route = useRoute()

const showTextDesktop = ref(state.isOpen)
const activeAccordionKey = ref('')

watch(() => state.isOpen, () => {
  if (state.isOpen) {
    setTimeout(() => {
      showTextDesktop.value = true
    }, 150)
  }
  else {
    showTextDesktop.value = false
  }
})

const showText = computed(() => {
  return process.server ? false : (isMobile.value || state.isOpen)
})

watch(isMobile, (newVal, oldVal) => {
  if (newVal && !oldVal && state.isOpen)
    state.toggleSidebar(false)
})

function handleAccordionClick(key: string) {
  if (key === activeAccordionKey.value) {
    activeAccordionKey.value = ''
  }
  else {
    activeAccordionKey.value = key
    state.toggleSidebar(true)
  }
}

onMounted(() => {
  if (isMobile.value)
    state.toggleSidebar(false)
})
</script>

<template>
  <div
    class="sidebar-left__wrapper"
    :class="{
      'is-active-mobile': (isMobile && state.isOpen),
      'is-active-desktop': (!isMobile),
    }"
  >
    <Drawer>
      <div
        class="flex flex-col gap-1.5 p-3.5"
      >
        <template v-if="isMobile">
          <DrawerItemAccordion :active-key="activeAccordionKey" item-key="prompt-library" label="Prompt Library" icon-class="px-0.5" :show-text="showText" @on-toggle="(key) => handleAccordionClick(key)">
            <IconPrompt
              w-7 h-7 transition-all class="!outline-none"
            />
            <template #content>
              <PromptLibraryMenu :active="true" />
            </template>
          </DrawerItemAccordion>

          <DrawerItemAccordion :active-key="activeAccordionKey" item-key="daily-bite" label="The Daily Bite" icon-class="px-0.5" :show-text="showText" @on-toggle="(key) => handleAccordionClick(key)">
            <IconEmailUnread
              class="w-[22px] h-22px" transition-all
            />
            <template #content>
              <TheDailyBite :active="true" />
            </template>
          </DrawerItemAccordion>
        </template>

        <DrawerLink to="/library" label="Library" icon-class="px-0.5" :show-text="showText" :is-active="route.path === '/library'" @click="() => state.toggleSidebar(false)">
          <div class="w-7 h-7 flex items-center justify-center">
            <IconLibrary
              class="w-6 h-6 flex-shrink-0"
              transition-all
            />
          </div>
        </DrawerLink>

        <DrawerLink to="/automations" label="Automations" icon-class="px-0.5" :show-text="showText" :is-active="route.path === '/automations'" @click="() => state.toggleSidebar(false)">
          <IconAutomation
            w-7 h-7 transition-all class="!outline-none"
          />
        </DrawerLink>

        <DrawerLink to="/prompts" label="Prompts" icon-class="px-0.5" :show-text="showText" :is-active="route.path === '/prompts'" @click="() => state.toggleSidebar(false)">
          <IconPrompt
            w-7 h-7 transition-all class="!outline-none"
          />
        </DrawerLink>

        <DrawerLink to="/visual-prompts" label="AI Images" :show-text="showText" disabled>
          <IconAIImages
            text="24px" transition-all class="!outline-none"
          />
        </DrawerLink>

        <DrawerLink to="" label="Earn with AI" :show-text="showText" disabled>
          <IconMoney
            text="24px" transition-all class="!outline-none"
          />
          <template #suffix>
            <BubbleComingSoon>
              Soon
            </BubbleComingSoon>
          </template>
        </DrawerLink>

        <DrawerLink to="https://www.make.com/en/integrations/snack-prompt" target="_blank" label="Make.com Integration" :show-text="showText">
          <IconMakeSmall
            text="24px" transition-all class="!outline-none"
          />
        </DrawerLink>

        <DrawerLink to="" label="Zapier Integration" :show-text="showText" disabled>
          <IconZapierSmall
            text="24px" transition-all class="!outline-none"
          />
          <template #suffix>
            <BubbleComingSoon>
              Soon
            </BubbleComingSoon>
          </template>
        </DrawerLink>
      </div>

      <CreateElementalMenu :is-mobile="isMobile" />
    </Drawer>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-left__wrapper {
  display: none;
  position: relative;

  @media screen and (max-width: 768px) {
    position: absolute;
    left: 0;
    top: 0;
    height: calc(100% - 57px);
    z-index: 999;
    width: 100%;
    padding-top: 61px;

    &.is-active-mobile {
      display: block;
    }
  }

  @media screen and (min-width: 769px) {
    &.is-active-desktop {
      display: flex;
    }
  }
}
</style>
